import React, { useEffect, useReducer, Fragment } from 'react'
import ReactDOM from 'react-dom'
import { object, func, string } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import ResponsiveLayout from '../../components/ui/responsive-layout/ResponsiveLayout'
import Button from '../../components/ui/button/Button'
import { urlPathToObject, objectToUrlPath, getQueryFromUrl } from '../../helpers/common'
import { EMPTY_OBJECT, RESPONSIVE_BREAKPOINTS, EMPTY_ARRAY,
  PROFESSIONALS, PAGEVIEW, PROJECTS, ARTICLE_ARCHIVE } from '../../utils/constants'
import { useWindowDimensions } from '../../providers/WindowDimensionsProvider'
import { useFiltersState } from '../../providers/filters/filtersContext'
import { useLocalStorage } from '../../helpers/hooks'
import {
  AREA_FILTERS,
  SMALL_FILTERS,
  BIG_FILTERS,
  FILTERS_DESKTOP_ORDER,
  FILTERS_MOBILE_ORDER,
  AREA,
  YEAR,
  BIM,
  KEYS_CAT_LEVEL_TWO,
  STANDALONE_FILTERS,
  COLOR,
  FILTERS_TREE_KEYS,
  STATES,
  TAG
} from './constants'
import Filter from './Filter'
import { messages } from './messages'
import { filtersReducer, filtersInitialState } from './filtersReducer'
import { getCatLabels, isCategoriesWithLevelsFilter, isCategoriesTreeFilter } from './helpers'
import { MobileHeader } from './partials'
import { sendEventToGTM } from '../../utils/tag_manager'
import { buildScopePath } from '../../utils/url_parser'

const INIT_PAGE = 1

/**
 * Container component for filters
 *
 * @component
 * @example
 * return (
 *   <Filters
 *    data={{
 *     companies: { name: "EGGER", slug: "egger", doc_count: 42 }
 *     year: { name: "2013", doc_count: 817 }
 *    }}
 *    history={{}}
 *    location={{}}
 *    request={() => {}}
 *    site='us',
 *    searchType='projects'
 *   />
 * )
*/
const Filters = ({ data = EMPTY_OBJECT, request, history, location, site, scope, searchType }) => {
  const { width } = useWindowDimensions() || window.innerWidth
  const { activeCategories = EMPTY_OBJECT, filtersObject } = useFiltersState()
  const [joinCategoriesLabels, setJoinCategoriesLabels, removeJoinCategoriesLabels] = useLocalStorage('joinCategoriesLabels')
  const [filterState, filtersDispatch] = useReducer(filtersReducer, filtersInitialState)
  const { isOpenOutside, isOpenInside, propsModal, activeFilterItem, filterButtonCount } = filterState
  const query = getQueryFromUrl(location)
  const dataIsValid = item => data.hasOwnProperty(item) && data[item].length > 0
  const catLabels = getCatLabels(data, searchType, activeCategories, joinCategoriesLabels)
  const uselocal = { joinCategoriesLabels, setJoinCategoriesLabels, removeJoinCategoriesLabels }
  useEffect(() => {
    if (!isOpenOutside || !isOpenInside) {
      if (width < RESPONSIVE_BREAKPOINTS.large) {
        document.body.style.overflow = 'unset'
        document.body.style.webkitOverflowScrolling = 'unset'
      }
    }
    const filtersButtonState = Object.keys(urlPathToObject(location, searchType, SMALL_FILTERS)).length
    const filterButtonAreaState = Object.keys(urlPathToObject(location, searchType, AREA_FILTERS)).length > 0 ? 1 : 0
    filtersDispatch({ type: 'SET_ACTIVE_FILTERS_COUNT', payload: filtersButtonState + filterButtonAreaState })
  }, [])

  const onShowFilter = item => {
    if (activeFilterItem === item) {
      filtersDispatch({ type: 'REMOVE_ACTIVE_INSIDE_FILTER' })
    } else {
      filtersDispatch({ type: 'SET_ACTIVE_INSIDE_FILTER', payload: item })
    }
  }

  const requestWithRouting = (filters = '') => {
    const trackingQueryParams = 'ad_medium=filters'
    const queryString = query ? `?q=${query}&${trackingQueryParams}` : `?${trackingQueryParams}`
    const sitePath = buildScopePath(scope)
    const slashCheckedFilters = filters.replace(' / ', ' slash ')

    if (searchType === PROJECTS) sendEventToGTM(searchType, PAGEVIEW, activeFilters(filters))
    history.push(`${sitePath}/${searchType}${slashCheckedFilters}${queryString}`)
    request(slashCheckedFilters, INIT_PAGE, query)
  }

  const activeFilters = filters => {
    const newLocation = location
    newLocation.pathname = searchType + filters

    return urlPathToObject(newLocation, searchType)
  }

  const onResetFilters = () => {
    const outsideFilters = urlPathToObject(location, searchType, BIG_FILTERS)
    const filters = objectToUrlPath(outsideFilters, searchType)
    requestWithRouting(filters)
  }

  const renderInsideFilter = () => {
    return <div className='ad-filter__inside-container'>
      <div className='ad-filter__menu'>
        {FILTERS_MOBILE_ORDER.hasOwnProperty([searchType])
          ? FILTERS_MOBILE_ORDER[searchType][4].map((item, index) => {
            const updatedProps = { ...propsModal, data: data[item], item, isOpenInside }
            if (dataIsValid(item) || item === AREA || item === COLOR || STANDALONE_FILTERS.includes(item)) {
              const contentClasses = classnames({ 'ad-filter__menu-content': true, 'active': activeFilterItem === item })
              return <Filter
                onShowFilter={onShowFilter}
                {...updatedProps}
                contentClasses={contentClasses}
                key={`${item}${index}mobile`}
              />
            }
          })
          : null
        }
      </div>
    </div>
  }
  const renderDesktop = (item, index) => {
    if (KEYS_CAT_LEVEL_TWO.includes(item) && joinCategoriesLabels) {
      return null
    }

    if (filtersObject?.country !== 'united-states' && site === 'us' && item === STATES) {
      return null
    }

    return <Filter
      key={item + index}
      request={request}
      data={data[item]}
      item={item}
      requestWithRouting={requestWithRouting}
      categoriesTreeData={isCategoriesTreeFilter(item, searchType) ? data[FILTERS_TREE_KEYS[searchType][item]] : EMPTY_ARRAY}
      categoriesLabels={catLabels}
      searchType={searchType}
      site={site}
      scope={scope}
      {...uselocal}
    />
  }

  return (
    <ResponsiveLayout
      renderLarge={() => (
        <div className='ad-filters'>
          {FILTERS_DESKTOP_ORDER.hasOwnProperty([searchType])
            ? FILTERS_DESKTOP_ORDER[searchType].map((item, index) =>
              dataIsValid(item) ||
                item === AREA ||
                item === COLOR ||
                STANDALONE_FILTERS.includes(item) ||
                isCategoriesWithLevelsFilter(item, searchType)
                ? renderDesktop(item, index)
                : null
            )
            : null}
        </div>
      )}
      renderSmall={() => (
        <Fragment>
          <div className='ad-filter__wrapper'>
            {FILTERS_MOBILE_ORDER.hasOwnProperty([searchType])
              ? FILTERS_MOBILE_ORDER[searchType].map((item, index) => {
                if (KEYS_CAT_LEVEL_TWO.includes(item) && joinCategoriesLabels) {
                  return null
                }
                if (filtersObject?.country !== 'united-states' && site === 'us' && item === STATES) {
                  return null
                }

                const modalProps = {
                  isMobile: true,
                  key: `${item}${index}mobile`,
                  request,
                  data: data[item],
                  item,
                  requestWithRouting,
                  onResetFilters,
                  categoriesLabels: catLabels,
                  searchType,
                  scope,
                  ...uselocal
                }
                if (item === TAG) modalProps.categoriesTreeData = data[FILTERS_TREE_KEYS[searchType][item]]

                const showAreaOutside = item === AREA && searchType === PROFESSIONALS
                const showYearOutside = item === YEAR && searchType === ARTICLE_ARCHIVE
                const filterButtonProps = { ...modalProps, isInside: true }
                if (dataIsValid(item) || item === BIM || showAreaOutside || showYearOutside || item === COLOR) {
                  return <Filter {...modalProps} onClick={() => filtersDispatch({ type: 'OPEN_OUTSIDE', payload: modalProps })} />
                } else if (typeof item === 'object') {
                  const filtersCount = `${filterButtonCount > 0 ? ' - ' + filterButtonCount : ''}`
                  return (<div key={item + index + 'mobile'}>
                    <Button
                      active={!!filterButtonCount}
                      onClick={() => filtersDispatch({ type: 'OPEN_OUTSIDE', payload: filterButtonProps })}
                    >
                      <FormattedMessage {...messages.filters} />
                      <span className='ad-filter__dash'>{filtersCount}</span>
                    </Button>
                  </div>)
                }
              })
              : null
            }
          </div>
          {isOpenOutside && ReactDOM.createPortal(
            <div className='ad-filter__mobile-container'>
              {<MobileHeader onResetFilters={onResetFilters} cancelCallback={() => filtersDispatch({ type: 'CLOSE_OUTSIDE' })} />}
              {propsModal.isInside
                ? renderInsideFilter()
                : <Filter {...propsModal} />}
            </div>
            , document.body)}
        </Fragment>
      )}
    />
  )
}

Filters.propTypes = {
  /**
   * This is Filters data
   */
  data: object,
  /**
   * Function that request the content for a selected filter
   */
  request: func,
  /**
   * Router's history
   */
  history: object,
  /**
   * Router's location
   */
  location: object,
  /**
   * Current site
   */
  site: string,
  scope: string,
  /**
   * Search's type
   */
  searchType: string
}

export default React.memo(Filters)
